import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { SimpleScheduleForm } from '@/components/schedule-wizard/simple-schedule-form';
import { schoolsService } from '@/services/schools.service';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Copy, Image, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import type { Schedule } from '@/types/api';

export function CreateSchedulePage() {
  const { schoolId } = useParams<{ schoolId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get('mode'); // 'copy' | 'parsed' | null
  
  const [initialSchedules, setInitialSchedules] = useState<Partial<Schedule>[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [createdCount, setCreatedCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Load preloaded schedule data from sessionStorage
  useEffect(() => {
    try {
      if (mode === 'copy') {
        const copiedData = sessionStorage.getItem('copiedSchedule');
        if (copiedData) {
          const parsed = JSON.parse(copiedData);
          // Convert date strings back to Date objects if present
          if (parsed.date) {
            parsed.date = new Date(parsed.date);
          }
          if (parsed.createdAt) {
            parsed.createdAt = new Date(parsed.createdAt);
          }
          if (parsed.updatedAt) {
            parsed.updatedAt = new Date(parsed.updatedAt);
          }
          setInitialSchedules([parsed]);
          sessionStorage.removeItem('copiedSchedule');
        }
      } else if (mode === 'parsed') {
        // Try multiple schedules first (new format)
        const parsedMultiple = sessionStorage.getItem('parsedSchedules');
        if (parsedMultiple) {
          const parsed = JSON.parse(parsedMultiple);
          setInitialSchedules(Array.isArray(parsed) ? parsed : [parsed]);
          sessionStorage.removeItem('parsedSchedules');
        } else {
          // Fall back to single schedule (old format)
          const parsedSingle = sessionStorage.getItem('parsedSchedule');
          if (parsedSingle) {
            const parsed = JSON.parse(parsedSingle);
            setInitialSchedules([parsed]);
            sessionStorage.removeItem('parsedSchedule');
          }
        }
      }
    } catch (error) {
      console.error('Failed to parse preloaded schedule data:', error);
      toast.error('Failed to load schedule data');
    } finally {
      setIsLoading(false);
    }
  }, [mode]);

  const currentSchedule = initialSchedules[currentIndex];
  const totalSchedules = initialSchedules.length;
  const isMultiple = totalSchedules > 1;

  const handleSave = async (schedule: Partial<Schedule>) => {
    try {
      await schoolsService.createSchedule(schoolId!, schedule);
      setCreatedCount(prev => prev + 1);
      
      if (currentIndex < totalSchedules - 1) {
        // More schedules to create
        toast.success(`Schedule "${schedule.displayName}" created! ${totalSchedules - currentIndex - 1} more to go.`);
        setCurrentIndex(prev => prev + 1);
      } else {
        // All done
        const total = createdCount + 1;
        toast.success(`${total} schedule${total > 1 ? 's' : ''} created successfully!`);
        navigate(`/dashboard/schools/${schoolId}#schedules`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to create schedule');
      throw error;
    }
  };

  const handleSkip = () => {
    if (currentIndex < totalSchedules - 1) {
      setCurrentIndex(prev => prev + 1);
    } else {
      // Done, go back
      if (createdCount > 0) {
        toast.success(`${createdCount} schedule${createdCount > 1 ? 's' : ''} created!`);
      }
      navigate(`/dashboard/schools/${schoolId}#schedules`);
    }
  };

  const handleCancel = () => {
    if (createdCount > 0) {
      toast.success(`${createdCount} schedule${createdCount > 1 ? 's' : ''} created!`);
    }
    navigate(`/dashboard/schools/${schoolId}#schedules`);
  };

  // Get subtitle based on mode
  const getSubtitle = () => {
    if (mode === 'copy') {
      return 'Creating from copied schedule';
    }
    if (mode === 'parsed' && isMultiple) {
      return `Creating ${totalSchedules} schedules from parsed image`;
    }
    if (mode === 'parsed') {
      return 'Creating from parsed image';
    }
    return 'Add a new schedule for this school';
  };

  // Show loading state while fetching preloaded data
  if (isLoading && mode) {
    return (
      <div className="container mx-auto max-w-4xl space-y-8 p-6">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-4xl space-y-8 p-6">
      <div className="flex items-center gap-3 animate-in fade-in-0 slide-in-from-top-4 duration-500">
        <Button 
          variant="outline" 
          size="icon"
          asChild
          className="h-10 w-10 rounded-lg transition-all hover:shadow-md hover:-translate-y-0.5"
        >
          <Link to={`/dashboard/schools/${schoolId}#schedules`}>
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight">Create Schedule</h1>
            {mode === 'copy' && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Copy className="h-3 w-3" />
                Copied
              </Badge>
            )}
            {mode === 'parsed' && (
              <Badge variant="secondary" className="flex items-center gap-1">
                <Image className="h-3 w-3" />
                From Image
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground mt-0.5">{getSubtitle()}</p>
        </div>
      </div>

      {/* Progress indicator for multiple schedules */}
      {isMultiple && (
        <Card className="border-dashed">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-sm font-medium">
                  Schedule {currentIndex + 1} of {totalSchedules}
                </span>
                {createdCount > 0 && (
                  <Badge variant="outline" className="text-green-600 border-green-600">
                    <Check className="h-3 w-3 mr-1" />
                    {createdCount} created
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
                  disabled={currentIndex === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <div className="flex gap-1">
                  {initialSchedules.map((_, idx) => (
                    <div
                      key={idx}
                      className={`h-2 w-2 rounded-full transition-colors ${
                        idx === currentIndex ? 'bg-primary' : 
                        idx < currentIndex ? 'bg-green-500' : 'bg-muted'
                      }`}
                    />
                  ))}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCurrentIndex(prev => Math.min(totalSchedules - 1, prev + 1))}
                  disabled={currentIndex === totalSchedules - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <Button variant="ghost" size="sm" onClick={handleSkip}>
                Skip this one
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <SimpleScheduleForm
        key={currentIndex} // Force re-render when switching schedules
        schoolId={schoolId!}
        existingSchedule={currentSchedule as Schedule | undefined}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </div>
  );
}